
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { LensFormValues } from "@/types/inventory";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "@/hooks/useTranslation";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarDays } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { LensNotesField } from "./form-fields/LensNotesField";

interface LensTabsFormProps {
  form: UseFormReturn<LensFormValues>;
  onSubmit: (values: LensFormValues) => Promise<void>;
  isEditing?: boolean;
}

export function LensTabsForm({ form, onSubmit, isEditing = false }: LensTabsFormProps) {
  const [activeTab, setActiveTab] = useState("essential");
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs defaultValue="essential" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="essential">{t('essential_info')}</TabsTrigger>
            <TabsTrigger value="optional">{t('optional_details')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="essential" className="space-y-4">
            {/* Essential Information */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('brand')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('brand_placeholder')} 
                        {...field} 
                        className={isMobile ? "text-base" : ""} 
                        maxLength={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="lens_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('lens_name')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('lens_name_placeholder')} 
                        {...field} 
                        className={isMobile ? "text-base" : ""} 
                        maxLength={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="mount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('Mount')}</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder={t('mount_placeholder')} 
                        {...field} 
                        className={`uppercase ${isMobile ? "text-base" : ""}`}
                        maxLength={4}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      {t('mount_description')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('status')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className={isMobile ? "text-base" : ""}>
                          <SelectValue placeholder={t('select_status')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent position={isMobile ? "popper" : "item-aligned"} className={isMobile ? "w-[calc(100vw-2rem)]" : ""}>
                        <SelectItem value="working">{t('working')}</SelectItem>
                        <SelectItem value="needs_repair">{t('needs_repair')}</SelectItem>
                        <SelectItem value="for_parts">{t('for_parts')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end pt-2">
                <Button type="button" onClick={() => setActiveTab("optional")}>
                  {t('next_step')}
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="optional" className="space-y-4">
            {/* Optional Information */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="serial_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('serial_number')}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        value={field.value || ''} 
                        className={isMobile ? "text-base" : ""} 
                        maxLength={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="purchase_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('purchase_date')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          type="date" 
                          {...field} 
                          value={field.value || ''} 
                          className={`${isMobile ? "text-base" : ""} pr-10`} 
                        />
                        <CalendarDays className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 pointer-events-none text-muted-foreground" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="purchase_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('purchase_price')}</FormLabel>
                    <FormControl>
                      <Input 
                        type="text" 
                        placeholder="0.00" 
                        formatPrice
                        {...field} 
                        value={field.value || ''}
                        className={isMobile ? "text-base" : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <LensNotesField form={form} />
              
              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" onClick={() => setActiveTab("essential")}>
                  {t('back')}
                </Button>
                <Button type="submit">
                  {isEditing ? t('update_lens') : t('add_lens')}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
}
